import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  SectionContent,
  Select,
  SimpleIcon,
  TextField,
} from "components";
import {
  manufacturerObj,
  osObj,
  departmentObj,
  // locationObj,
  rolesObj,
} from "utils/constants";
import { PageContainer } from "shared";
import { useUpdateDevice } from "api/actions/auth";
import { DeviceUserList } from "./DeviceList";
import { useSelector } from "react-redux";
import { notification } from "utils";

const Icon = ({ icon }) => (
  <SimpleIcon icon={icon} frontColor="text-gray-300" fontSize={18} />
);

export default function ManageDevices() {
  const [deviceData, setDeviceData] = useState({});
  const [deviceId, setDeviceId] = useState(null);
  const [manufacturer, setManufacturer] = useState(null);
  const [osName, setOsName] = useState(null);
  const [department, setDepartment] = useState(null);
  // const [location, setLocation] = useState(null);
  const [allowedRole, setAllowedRole] = useState(null);
  const { current_user } = useSelector((state) => state.auth);

  const [isLoading, isLoaded, fetchDeviceInfo, updateDevice] =
    useUpdateDevice();

  const handleDetails = (e) => {
    if (e.target.name === "deviceId") {
      setDeviceId(e.target.value);
    }
    setDeviceData((prevS) => ({
      ...prevS,
      [e.target.name]: e.target.value,
    }));
  };

  const handleDeviceUpdate = () => {
    if (!deviceId) {
      notification("danger", "Please enter a Device ID");
      return;
    }

    fetchDeviceInfo(deviceData?.baseDeviceHash);
    updateDevice(deviceId, deviceData, current_user);
  };

  const getDeviceData = useCallback(() => {
    return deviceData;
  }, [deviceData])();

  useEffect(() => {
    if (isLoaded) {
      setDeviceData({});
      setManufacturer("");
      setOsName("");
      setDepartment("");
      // setLocation("");
      setAllowedRole("");
      setDeviceId("");
    }
  }, [isLoaded]);

  return (
    <PageContainer>
      <SectionContent otherClass="grid grid-cols-2 gap-3">
        <div className="w-full col-span-1 p-2 border rounded shadow-sm border-slate-100 dark:border-gray-500 dark:bg-gray-700">
          <div className="p-0 rounded shadow-sm">
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-2 p-4 rounded dark:bg-gray-900">
                <div className="grid grid-cols-1 gap-6">
                  {/* Device ID */}
                  <div className="col-span-1">
                    <TextField
                      label="Device ID"
                      placeholder="Enter Device ID to Update"
                      name="deviceId"
                      value={getDeviceData?.deviceId || ""}
                      handleChange={handleDetails}
                      startAdornment={<Icon icon="Computer" />}
                      disableAutoComplete
                    />
                  </div>

                  {/* Device base hash */}

                  <div className="col-span-1">
                    <TextField
                      label="Base Device Hash"
                      placeholder="Enter Base Device Hash"
                      name="baseDeviceHash"
                      value={getDeviceData?.baseDeviceHash || ""}
                      handleChange={handleDetails}
                      startAdornment={<Icon icon="Computer" />}
                      disableAutoComplete
                    />
                  </div>

                  {/* Serial Number */}
                  <div className="col-span-1">
                    <TextField
                      label="Serial Number"
                      placeholder="Device Serial Number"
                      name="serialNumber"
                      value={getDeviceData?.serialNumber || ""}
                      handleChange={handleDetails}
                      startAdornment={<Icon icon="Computer" />}
                      disableAutoComplete
                    />
                  </div>

                  {/* Manufacturer */}
                  <div className="col-span-1">
                    <Select
                      inputLabel="Manufacturer"
                      name="manufacturer"
                      dropDown={manufacturerObj}
                      handleChange={(e) => {
                        // console.log(e, "eeeee");
                        setDeviceData((prevS) => ({
                          ...prevS,
                          manufacturer: e.value,
                        }));
                        setManufacturer(e.value);
                      }}
                      valueAlt={manufacturer}
                      setValueAlt={setManufacturer}
                      startAdornment={<Icon icon="Factory" />}
                    />
                  </div>

                  {/* Model Number */}
                  <div className="col-span-1">
                    <TextField
                      label="Model Number"
                      placeholder="Device Model Number"
                      name="modelNumber"
                      value={getDeviceData?.modelNumber || ""}
                      handleChange={handleDetails}
                      startAdornment={<Icon icon="DisplaySettings" />}
                      disableAutoComplete
                    />
                  </div>

                  {/* MAC Address */}
                  <div className="col-span-1">
                    <TextField
                      label="MAC Address"
                      placeholder="Device MAC Address"
                      name="macAddress"
                      value={getDeviceData?.macAddress || ""}
                      handleChange={handleDetails}
                      startAdornment={<Icon icon="Dns" />}
                      disableAutoComplete
                    />
                  </div>

                  {/* Operating System */}
                  <div className="col-span-1">
                    <Select
                      inputLabel="Operating System"
                      name="operatingSystem"
                      dropDown={osObj}
                      handleChange={(e) =>
                        setDeviceData((prevS) => ({
                          ...prevS,
                          osName: e.value,
                        }))
                      }
                      valueAlt={osName || ""}
                      setValueAlt={setOsName}
                      startAdornment={<Icon icon="DeveloperBoard" />}
                    />
                  </div>

                  {/* RAM Size */}
                  <div className="col-span-1">
                    <TextField
                      label="RAM Size (GB)"
                      placeholder="RAM in Gigabytes"
                      name="ramSize"
                      type="number"
                      value={getDeviceData?.ramSize || ""}
                      handleChange={handleDetails}
                      startAdornment={<Icon icon="Memory" />}
                      disableAutoComplete
                    />
                  </div>

                  {/* Storage Size */}
                  <div className="col-span-1">
                    <TextField
                      label="Storage Size (GB)"
                      placeholder="Storage in Gigabytes"
                      name="storageSize"
                      type="number"
                      value={getDeviceData?.storageSize || ""}
                      handleChange={handleDetails}
                      startAdornment={<Icon icon="Save" />}
                      disableAutoComplete
                    />
                  </div>

                  {/* Department */}
                  <div className="col-span-1">
                    <Select
                      inputLabel="Department"
                      name="department"
                      dropDown={departmentObj}
                      handleChange={(e) =>
                        setDeviceData((prevS) => ({
                          ...prevS,
                          department: e.value,
                        }))
                      }
                      valueAlt={department || ""}
                      setValueAlt={setDepartment}
                      startAdornment={<Icon icon="AccountBox" />}
                    />
                  </div>

                  {/* Location */}
                  {/* <div className="col-span-1">
                    <Select
                      inputLabel="Location"
                      name="location"
                      dropDown={locationObj}
                      handleChange={(e) =>
                        setDeviceData((prevS) => ({
                          ...prevS,
                          location: e.value,
                        }))
                      }
                      valueAlt={location || ""}
                      setValueAlt={setLocation}
                      startAdornment={<Icon icon="LocationOn" />}
                    />
                  </div> */}

                  {/* Allowed Roles */}
                  <div className="col-span-1">
                    <Select
                      inputLabel="Allowed Roles"
                      name="allowedRoles"
                      dropDown={rolesObj}
                      handleChange={(e) =>
                        setDeviceData((prevS) => ({
                          ...prevS,
                          allowedRoles: e.value,
                        }))
                      }
                      valueAlt={allowedRole || ""}
                      setValueAlt={setAllowedRole}
                      startAdornment={<Icon icon="SupervisorAccount" />}
                    />
                  </div>

                  {/* Register Button */}
                  <div className="col-span-1">
                    <Button
                      label="Update Device"
                      fullWidth
                      gradient
                      isSubmitting={isLoading}
                      handleClick={handleDeviceUpdate}
                      disabled={!deviceId}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-span-1 h-[calc(140vh-100px)] overflow-y-scroll">
          <DeviceUserList />
        </div>
      </SectionContent>
    </PageContainer>
  );
}
